import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Typography, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  FormControlLabel, Checkbox, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { generateMasterDataTemplate, parseMasterDataWorkbook, validateMasterData } from '@timetable/utils';
import { getTeacherTypesDirect, fetchExistingMasterDataDirect } from '@timetable/api';
import { useAuthStore } from '@/store/authStore';
import type { WizardState, WizardAction } from '../ImportWizard';

interface Step1Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  onNext: (patch?: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
}

export function Step1MasterData({ state, dispatch, onNext, onBack }: Step1Props) {
  const { schoolContext } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [prefillExisting, setPrefillExisting] = useState(true);
  const [fetchingExisting, setFetchingExisting] = useState(false);

  const { data: teacherTypes } = useQuery({
    queryKey: ['teacher-types'],
    queryFn: getTeacherTypesDirect,
  });

  const handleDownloadTemplate = async () => {
    let snapshot = state.parsedMasterData ?? {
      teachers: [], subjects: [], classes: [], rooms: [], schedules: [], scheduleAssignments: [],
      teacherSubjects: [], teacherClasses: [], subjectClasses: [],
    };

    if (prefillExisting && schoolContext) {
      setFetchingExisting(true);
      try {
        snapshot = await fetchExistingMasterDataDirect(schoolContext);
      } catch (err) {
        setParseError(err instanceof Error ? `Could not load existing data: ${err.message}` : 'Could not load existing data.');
        setFetchingExisting(false);
        return;
      }
      setFetchingExisting(false);
    }

    const typeNames = teacherTypes?.map((t) => t.type_name) ?? [];
    const blob = generateMasterDataTemplate(snapshot, typeNames.length > 0 ? typeNames : undefined);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `master-data-template-${schoolContext ?? 'school'}-${state.selectedAcademicYear || 'draft'}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      setParseError('Only .xlsx files are accepted.');
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const parsed = await parseMasterDataWorkbook(file);
      const validation = validateMasterData(parsed);
      dispatch({ type: 'SET_MASTER_DATA', payload: { file, parsed, validation } });
    } catch (err: unknown) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse file.');
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  const validation = state.masterDataValidation;
  const canAdvance = validation?.valid === true;

  const handleNext = async () => {
    await onNext({
      master_data_json: state.parsedMasterData,
      validation_status: 'valid',
    });
  };

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Step 1: Master Data</Typography>

      {/* Download template */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={prefillExisting}
                onChange={(e) => setPrefillExisting(e.target.checked)}
                size="small"
              />
            }
            label="Pre-fill template with existing school data"
            sx={{ m: 0 }}
          />
          <Tooltip title="When checked, the downloaded template will include all teachers, subjects, classes, class schedules and teacher-subject mappings already in the system. Uncheck to get a blank template.">
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Button
          variant="outlined"
          startIcon={fetchingExisting ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={handleDownloadTemplate}
          disabled={fetchingExisting}
          sx={{ alignSelf: 'flex-start' }}
        >
          {fetchingExisting ? 'Fetching data…' : 'Download Master Data Template'}
        </Button>
      </Box>

      {/* Drop zone */}
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Drag &amp; drop your .xlsx file here, or click to browse
        </Typography>
        {state.masterDataFile && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
            {state.masterDataFile.name}
          </Typography>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChange(f); }}
        />
      </Box>

      {parsing && <CircularProgress size={24} />}
      {parseError && <Alert severity="error">{parseError}</Alert>}

      {/* Validation report */}
      {validation && !validation.valid && (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {validation.errors.length} error(s) found. Fix them before proceeding.
          </Alert>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tab</TableCell>
                  <TableCell>Row</TableCell>
                  <TableCell>Column</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validation.errors.map((err, i) => (
                  <TableRow key={i}>
                    <TableCell>{err.tab ?? '—'}</TableCell>
                    <TableCell>{err.row}</TableCell>
                    <TableCell>{err.column}</TableCell>
                    <TableCell>{err.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {validation?.valid && (
        <Alert severity="success">File validated successfully.</Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack} sx={{ visibility: 'hidden' }}>Back</Button>
        <Button variant="contained" disabled={!canAdvance} onClick={handleNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
}
